import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import { deleteCommissionLine, updateCommissionLine } from '../../Services/partners';
import { Commission, displayStatus, eur, fmtDate, isEditable } from './commissionFormat';

// Détail d'une ligne de commission, et retouche ou suppression d'une ligne non réglée.
//
// Une ligne porte le taux de son AFFAIRE, figé au règlement. La retouche sert les cas
// qui ne valent que pour cette ligne : une négociation, une règle particulière. Pour
// changer le taux de tous les règlements à venir, c'est « Termes » sur l'affaire.
//
// « Forcer » marque la ligne comme décidée à la main : le recalcul des commissions ne la
// touchera jamais. Il ne retarife aujourd'hui aucune ligne existante ; le drapeau est la
// garde du jour où il le fera.
//
// Supprimer est réel : la facture redevient « non commissionnée », et le recalcul (ou un
// paiement rejoué) recréera la ligne au taux de l'affaire si l'encaissement est dans sa
// fenêtre. Voulu : c'est la façon de « refaire » une ligne.

type Props = {
	commission: Commission | null;
	clientName?: string;
	onClose: () => void;
	onSaved: () => void;
};

// #region DetailRow
const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Box sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{children}</Box>
	</Box>
);
// #endregion

// #region CommissionDetails — tout ce que porte la ligne, en lecture
const CommissionDetails: React.FC<{ c: Commission; clientName?: string }> = ({ c, clientName }) => {
	const st = displayStatus(c);
	return (
		<Box>
			<DetailRow label="Client">{clientName || c.billingAccountId}</DetailRow>
			<DetailRow label="Affaire">{c.attributionId ? `#${c.attributionId}` : '—'}</DetailRow>
			<DetailRow label="Origine">
				{c.invoiceId ? (
					<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
						{c.invoiceId}
					</Typography>
				) : (
					<Chip label="Règlement saisi à la main" size="small" variant="outlined" />
				)}
			</DetailRow>
			<DetailRow label="Encaissé le">{fmtDate(c.collectedAt)}</DetailRow>
			<DetailRow label="Encaissé HT">{eur(c.collectedAmount)}</DetailRow>
			<DetailRow label="Taux appliqué">{c.rate ?? 0} %</DetailRow>
			{c.originalRate != null && <DetailRow label="Taux d'origine">{c.originalRate} %</DetailRow>}
			<DetailRow label="Commission">
				<strong>{eur(c.amount)}</strong>
			</DetailRow>
			<DetailRow label="Statut">
				<Chip label={st.label} size="small" color={st.color} />
			</DetailRow>
			<DetailRow label="Déblocable le">{fmtDate(c.unlockableAt)}</DetailRow>
			{c.payoutId && <DetailRow label="Versement">#{c.payoutId}</DetailRow>}
			<DetailRow label="Créée le">{fmtDate(c.createdAt)}</DetailRow>
			{c.editedAt && (
				<DetailRow label="Retouchée">
					<Typography variant="body2">
						{fmtDate(c.editedAt)} · {c.editedBy || '—'}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{c.editNote}
					</Typography>
				</DetailRow>
			)}
		</Box>
	);
};
// #endregion

// #region useCommissionEdit — état du formulaire, réinitialisé à chaque ouverture
const useCommissionEdit = (c: Commission | null, onDone: () => void) => {
	const notify = useNotify();
	const [rate, setRate] = useState('');
	const [forced, setForced] = useState(true);
	const [note, setNote] = useState('');
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);

	// Réinitialisé sur la ligne courante : sans ça, ouvrir une deuxième ligne afficherait
	// le taux de la première, et on le poserait sur la mauvaise commission.
	useEffect(() => {
		if (!c) return;
		setRate(String(c.rate ?? 0));
		setForced(c.editedAt ? !!c.forced : true);
		setNote('');
		setConfirmDelete(false);
	}, [c]);

	const parsed = Number(rate);
	const rateInvalid = rate === '' || Number.isNaN(parsed) || parsed < 0 || parsed > 100;
	const invalid = rateInvalid || note.trim() === '';

	const run = (p: Promise<unknown>, ok: string) => {
		setSaving(true);
		p.then(() => {
			notify(ok, { type: 'success' });
			onDone();
		})
			.catch((e: any) => notify(`Erreur : ${e?.message || 'opération impossible'}`, { type: 'error' }))
			.finally(() => setSaving(false));
	};

	const save = () => {
		if (!c || invalid) return;
		run(updateCommissionLine(c.id, { rate: parsed, forced, note: note.trim() }), 'Commission mise à jour');
	};

	// Deux clics : le premier arme, le second supprime.
	const remove = () => {
		if (!c) return;
		if (!confirmDelete) return setConfirmDelete(true);
		run(deleteCommissionLine(c.id), 'Ligne supprimée');
	};

	return { rate, setRate, forced, setForced, note, setNote, saving, parsed, rateInvalid, invalid, save, confirmDelete, remove };
};
// #endregion

// #region EditCommissionDialog
export const EditCommissionDialog: React.FC<Props> = ({ commission, clientName, onClose, onSaved }) => {
	const f = useCommissionEdit(commission, () => {
		onSaved();
		onClose();
	});
	if (!commission) return null;
	const editable = isEditable(commission);
	// Aperçu du montant : même formule que l'API (encaissé × taux), pour décider en voyant
	// ce qui sera dû au partenaire.
	const preview = f.rateInvalid ? null : Math.round((commission.collectedAmount || 0) * f.parsed) / 100;

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Ligne de commission #{commission.id}</DialogTitle>
			<DialogContent>
				<CommissionDetails c={commission} clientName={clientName} />
				<Divider sx={{ my: 2 }} />
				{!editable ? (
					<Alert severity="info">
						Cette ligne est engagée dans un versement : elle ne se modifie ni ne se supprime plus. Une correction passe par un
						ajustement du wallet.
					</Alert>
				) : (
					<CommissionEditForm f={f} preview={preview} />
				)}
				{f.confirmDelete && (
					<Alert severity="warning" sx={{ mt: 2 }}>
						La ligne sera supprimée. Le recalcul des commissions la recréera au taux de l'affaire si l'encaissement est dans sa
						fenêtre.
					</Alert>
				)}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
					<Box>
						{editable && (
							<Button color="error" onClick={f.remove} disabled={f.saving}>
								{f.confirmDelete ? 'Confirmer la suppression' : 'Supprimer la ligne'}
							</Button>
						)}
					</Box>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button onClick={onClose}>Fermer</Button>
						{editable && (
							<Button variant="contained" onClick={f.save} disabled={f.saving || f.invalid}>
								Enregistrer
							</Button>
						)}
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
// #endregion

// #region CommissionEditForm
const CommissionEditForm: React.FC<{ f: ReturnType<typeof useCommissionEdit>; preview: number | null }> = ({
	f,
	preview,
}) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<TextField
			size="small"
			type="number"
			label="Taux de commission"
			value={f.rate}
			onChange={(e) => f.setRate(e.target.value)}
			error={f.rateInvalid}
			helperText={preview === null ? 'Entre 0 et 100.' : `Commission : ${eur(preview)}`}
			InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
		/>
		<FormControlLabel
			control={<Checkbox checked={f.forced} onChange={(e) => f.setForced(e.target.checked)} />}
			label="Forcer : ne jamais recalculer cette ligne"
		/>
		<TextField
			size="small"
			label="Motif"
			value={f.note}
			onChange={(e) => f.setNote(e.target.value)}
			multiline
			minRows={2}
			required
			helperText="Obligatoire : c'est de l'argent dû à un tiers, la raison doit se relire plus tard."
		/>
	</Box>
);
// #endregion
